import React, { useState, useContext } from 'react';
import CandidateContainer from './CandidateContainer';
import { DutyPlannerContext } from './DutyPlanner';
import { ButtonGroup, Button, Accordion, Form } from '../../ui-kit';
import { IconXmark } from '../../ui-kit/icons';
import { orderBy } from 'lodash';

const CollapsibleList = ({ key, title, children, defaultExpanded }) => (
  <Accordion
    itemKey={key}
    headerContent={title}
    bodyContent={children}
    defaultActiveKeys={defaultExpanded && [key]}
    alwaysOpen={defaultExpanded}
  />
);

const CandidateList = ({ plannerMode, setPlannerMode, assign, periodStartTime, periodEndTime }) => {
  const dutyPlannerContext = useContext(DutyPlannerContext);
  const allStations = dutyPlannerContext.stations || [];
  const stationSettings = dutyPlannerContext.stationSettings || {};
  const multiStation = allStations.length > 1;
  const dutyOnlyStations = allStations.filter(s => !!s.default_duty_planner_duty_function_id);

  // If we are in duty mode, we only want to show stations for which we have a default duty function set up
  const stations = plannerMode == 'duty' ? dutyOnlyStations : allStations;

  return (
    <>
      {stations.map(station => (
        <StationCandidateList
          key={station.id}
          station={station}
          multiStation={multiStation}
          settings={stationSettings[station.id] || {}}
          setPlannerMode={setPlannerMode}
          plannerMode={plannerMode}
          assign={assign}
          periodStartTime={periodStartTime}
          periodEndTime={periodEndTime}
        />
      ))}
    </>
  );
};

const StationCandidateList = ({
  station,
  multiStation,
  settings,
  plannerMode,
  setPlannerMode,
  assign,
  periodStartTime,
  periodEndTime
}) => {
  const [filterString, setFilterString] = useState('');

  const functions = settings.functions || [];
  const candidateUsers = settings.candidateUsers || [];
  const usersOffDuty = settings.usersOffDuty || [];
  const usersOnCall = settings.usersOnCall || [];
  const defaultFunction = settings.defaultFunction || null;
  const multiFunction = settings.allowMultipleFunctions;

  const applyFilterToOffDuty = () => {
    if (!filterString || filterString === '') return usersOffDuty;
    const regex = new RegExp(`.*${filterString.toLowerCase()}.*`, 'g');
    return usersOffDuty.filter(u => {
      const fullName = `${u.first_name} ${u.last_name}`;
      return regex.test(fullName.toLowerCase()) || regex.test(u.nickname.toLowerCase());
    });
  };

  const filteredUsersOffDuty = applyFilterToOffDuty();

  const unsortedCandidates = plannerMode === 'duty' ? candidateUsers : usersOnCall;

  const sortedCandidates = orderBy(unsortedCandidates, ['availabilityLength', 'displayOrder'], ['desc', 'asc']);

  // the number of non-candidates (off duty) regardless of filtering
  const usersOffDutyCount = usersOffDuty.length;
  // the number of candidates (on duty as function) who are at least available
  // for some period of time. This way, the planner knows how many users they
  // still have to plan.
  const candidatesCount = sortedCandidates.filter(u => (u.availabilityLength || 0) > 0).length;

  const candidatesTableTitle = `${I18n.t(`duty_planner.candidates.${plannerMode}`, {
    func: defaultFunction?.name || 'unknown'
  })} (${candidatesCount})`;
  const usersOffDutyTitle = `${I18n.t('duty_planner.candidates.off_duty')} (${usersOffDutyCount})`;

  return (
    <div className="mb-4">
      {multiStation && <h5>{station.name}</h5>}
      {defaultFunction && (
        <ButtonGroup className="d-print-none w-100 d-flex mb-3">
          <Button
            onClick={() => setPlannerMode('duty')}
            variant={plannerMode === 'duty' ? 'primary' : 'outline-secondary'}
          >
            {I18n.t('duty_planner.candidates.duty', { func: defaultFunction?.name || '' })}
          </Button>
          <Button
            onClick={() => setPlannerMode('on_call')}
            variant={plannerMode === 'on_call' ? 'success' : 'outline-secondary'}
          >
            {I18n.t('duty_planner.candidates.on_call')}
          </Button>
        </ButtonGroup>
      )}{' '}
      <CollapsibleList defaultExpanded={!multiStation} title={candidatesTableTitle}>
        {candidateUsers.length === 0 && <i>{I18n.t('no_results_found')}</i>}
        <div className="list-group candidates">
          {sortedCandidates.map(user => (
            <CandidateContainer
              user={user}
              assign={assign}
              multiFunction={multiFunction}
              key={user.id}
              stationId={station.id}
              isCandidate={true}
              periodStartTime={periodStartTime}
              periodEndTime={periodEndTime}
              functions={functions}
            />
          ))}
        </div>
      </CollapsibleList>
      <br />
      {plannerMode === 'duty' && (
        <CollapsibleList title={usersOffDutyTitle} defaultExpanded={!multiStation}>
          <Form.Group className="mb-3">
            <Form.InputGroup>
              <Form.Control
                type="text"
                placeholder={I18n.t('search')}
                value={filterString}
                onChange={event => setFilterString(event.target.value)}
              />
              <Form.InputGroupText onClick={() => setFilterString('')}>
                <IconXmark />
              </Form.InputGroupText>
            </Form.InputGroup>
          </Form.Group>
          {filteredUsersOffDuty.length === 0 && <i>{I18n.t('no_results_found')}</i>}
          {filteredUsersOffDuty.length > 0 && (
            <p className="small mb-2">{I18n.t('duty_planner.turn_into_candidate')}</p>
          )}
          <div className="list-group users-off-duty">
            {filteredUsersOffDuty.map(user => (
              <CandidateContainer
                user={user}
                settings={settings}
                assign={assign}
                key={user.id}
                isCandidate={false}
                periodStartTime={periodStartTime}
                periodEndTime={periodEndTime}
              />
            ))}
          </div>
        </CollapsibleList>
      )}
    </div>
  );
};

export default CandidateList;
